import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { isTypingTarget } from '@/lib/typingTarget'

// Global keyboard shortcuts
export function useKeyboardShortcuts() {
  const navigate = useNavigate()

  useEffect(() => {
    function handle(e: KeyboardEvent) {
      // Skip if the user is typing. This used to be a tag-name check against
      // INPUT/TEXTAREA/SELECT, which misses the Lexical compose body — a
      // contenteditable DIV — see lib/typingTarget.
      if (isTypingTarget(e.target)) return

      // Skip if command palette is handling it
      if (e.metaKey || e.ctrlKey) return

      switch (e.key) {
        case 'g':
          // g then next key for "go to" navigation
          waitForNext(e, {
            'h': () => navigate('/'),
            'c': () => navigate('/contacts'),
            'l': () => navigate('/calls'),
            't': () => navigate('/transactions'),
            'p': () => navigate('/properties'),
            'r': () => navigate('/ccr-library'),
            'd': () => navigate('/documents'),
            'f': () => navigate('/follow-up'),
            'i': () => navigate('/inbox'),
            'a': () => navigate('/analytics'),
            's': () => navigate('/settings'),
          })
          break
        case 'n':
          // n for "new" - opens create dialogs
          // Dispatched as custom events, pages listen for them
          window.dispatchEvent(new CustomEvent('rize:shortcut', { detail: 'new' }))
          break
        case '[':
          // Collapse/expand the left sidebar (tracker row 67). The Sidebar owns
          // the state and its persistence; this only rings the bell, which is
          // why the binding can live here with the rest of them.
          e.preventDefault()
          window.dispatchEvent(new CustomEvent('rize:shortcut', { detail: 'toggle-sidebar' }))
          break
        case '?':
          window.dispatchEvent(new CustomEvent('rize:shortcut', { detail: 'help' }))
          break
      }
    }

    window.addEventListener('keydown', handle)
    return () => window.removeEventListener('keydown', handle)
  }, [navigate])
}

// Wait for the next key press within 500ms
function waitForNext(firstEvent: KeyboardEvent, handlers: Record<string, () => void>) {
  firstEvent.preventDefault()

  function onNext(e: KeyboardEvent) {
    cleanup()
    const fn = handlers[e.key]
    if (fn) {
      e.preventDefault()
      fn()
    }
  }

  function cleanup() {
    window.removeEventListener('keydown', onNext)
    clearTimeout(timer)
  }

  const timer = setTimeout(cleanup, 500)
  window.addEventListener('keydown', onNext, { once: true })
}
